import { useApolloClient } from '@apollo/client'
import {
  Box,
  Button,
  Variants as ButtonVariants,
  MediumIcon as MediumType,
} from '@factorialco/deprecated-design-system'
import { PayrollBundleInHouseIncidencePageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import useIncidenceRuleEvaluation from 'modules/bookkeepers/hooks/useIncidenceRuleEvaluation'
import { useUpdateIncidenceStatus } from 'modules/bookkeepers/hooks/useUpdateIncidenceStatus'
import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import {
  FACTORIAL_APP,
  PROFESSIONALS_APP,
  trackIncidenceChangeStatus,
} from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { statusTranslation } from 'modules/bookkeepers/lib/dynamicTranslations'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import {
  IncidenceStatus,
  isValidIncidenceStatus,
} from 'modules/payroll_bundle/lib/Incidences/Status'

const FLASH_MESSAGE_TYPES = {
  SUCCESS: 'correct',
} as const

const REFETCH_QUERIES = [PayrollBundleInHouseIncidencePageDocument]

interface ActionsButtonsDataType {
  translationString: string
  type: ButtonVariants
  canBeDisabled: boolean
  nextStatus: IncidenceStatus
  icon: MediumType
  bookkeeperResponsibility: boolean
  inHousePayroll: boolean
  currentStatuses: IncidenceStatus[]
  style?: Partial<CSSStyleDeclaration>
}

const actionsButtonsData: () => ActionsButtonsDataType[] = () => [
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_discard'),
    type: 'secondary',
    canBeDisabled: false,
    nextStatus: 'discarded',
    icon: 'deny',
    bookkeeperResponsibility: false,
    inHousePayroll: false,
    currentStatuses: ['in-preparation'],
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_admin.to_do'),
    type: 'primary',
    canBeDisabled: false,
    nextStatus: 'in-preparation',
    icon: 'arrow-left',
    bookkeeperResponsibility: false,
    inHousePayroll: false,
    currentStatuses: ['discarded'],
  },
  {
    translationString: i18n.t(
      'bookkeepers.incidence.bulk_status_changes.actions_menu.send_to_bookkeeper'
    ),
    type: 'primary',
    canBeDisabled: true,
    nextStatus: 'to-do',
    icon: 'arrow-right',
    bookkeeperResponsibility: false,
    inHousePayroll: false,
    currentStatuses: ['in-preparation'],
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_discard'),
    type: 'secondary',
    canBeDisabled: false,
    nextStatus: 'discarded',
    icon: 'deny',
    bookkeeperResponsibility: true,
    inHousePayroll: false,
    currentStatuses: ['to-do', 'doing', 'done'],
    style: { position: 'absolute', left: '16px' },
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_bookkeeper.to_do'),
    type: 'primary',
    canBeDisabled: false,
    nextStatus: 'to-do',
    icon: 'arrow-left',
    bookkeeperResponsibility: true,
    inHousePayroll: false,
    currentStatuses: ['discarded', 'doing', 'done'],
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_bookkeeper.doing'),
    type: 'primary',
    canBeDisabled: false,
    nextStatus: 'doing',
    icon: 'pencil',
    bookkeeperResponsibility: true,
    inHousePayroll: false,
    currentStatuses: ['discarded', 'to-do', 'done'],
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_bookkeeper.done'),
    type: 'primary',
    canBeDisabled: false,
    nextStatus: 'done',
    icon: 'check',
    bookkeeperResponsibility: true,
    inHousePayroll: false,
    currentStatuses: ['discarded', 'to-do', 'doing'],
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_discard'),
    type: 'secondary',
    canBeDisabled: false,
    nextStatus: 'discarded',
    icon: 'deny',
    bookkeeperResponsibility: false,
    inHousePayroll: true,
    currentStatuses: ['to-do', 'doing'],
    style: { position: 'absolute', left: '16px' },
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_inhouse.mark_doing'),
    type: 'primary',
    canBeDisabled: false,
    nextStatus: 'doing',
    icon: 'arrow-right',
    bookkeeperResponsibility: false,
    inHousePayroll: true,
    currentStatuses: ['to-do'],
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_inhouse.mark_done'),
    type: 'primary',
    canBeDisabled: false,
    nextStatus: 'done',
    icon: 'check',
    bookkeeperResponsibility: false,
    inHousePayroll: true,
    currentStatuses: ['to-do', 'doing'],
  },
  {
    translationString: i18n.t('bookkeepers.incidence.actions_buttons_admin.to_do'),
    type: 'primary',
    canBeDisabled: false,
    nextStatus: 'to-do',
    icon: 'arrow-left',
    bookkeeperResponsibility: false,
    inHousePayroll: true,
    currentStatuses: ['discarded'],
  },
]
interface ActionsButtonsProps {
  updateIncidenceStatus?: (newStatus: string) => void
}

const ActionsButtons = ({ updateIncidenceStatus }: ActionsButtonsProps) => {
  const isBookkeeperView = useBookkeeperView()
  const { hasInhousePayroll } = useInhousePayroll()
  const { hasCompensations: hasCompensationsModule } = useHasCompensations()
  const incidence = useCurrentIncidence()
  const apolloClient = useApolloClient()
  const { ruleEvaluation } = useIncidenceRuleEvaluation(
    incidence.id,
    incidence.employeeId || 0,
    incidence.legalEntityId || 0,
    incidence.legalEntity?.companyId || 0
  )
  const { update } = useUpdateIncidenceStatus()
  const changeToNewStatus = async (newStatus: IncidenceStatus) => {
    const previousStatus = incidence.status
    const translatedStatusName = statusTranslation(newStatus, isBookkeeperView)
    const variables = {
      newIncidenceStatus: newStatus,
      incidenceId: incidence.id,
    }
    const result = await update(variables)
    const { success } = handleResult(result, () => undefined)
    if (!success) return

    if (updateIncidenceStatus) {
      updateIncidenceStatus(newStatus)
    }

    updateIncidencesListStatus(incidence, newStatus)

    await apolloClient.refetchQueries({
      include: REFETCH_QUERIES,
    })
    trackIncidenceChangeStatus(incidence, newStatus, isBookkeeperView, hasInhousePayroll)

    if (hasCompensationsModule) {
      track(trackingEvents.compensations.changeStatusEmployeeUpdates, {
        previous_status: previousStatus,
        new_status: newStatus,
        application: isBookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
      })
    }

    app.setFlash({
      type: FLASH_MESSAGE_TYPES.SUCCESS,
      message: i18n.t('bookkeepers.incidence.marked_as_message', { status: translatedStatusName }),
    })
  }

  // This method ensures that, once the status is updated, the change is reflected on
  // the incidences table (which doesn't use models, but GraphQL objects)
  const updateIncidencesListStatus = (
    incidenceData: IncidenceQueryData,
    newStatus: IncidenceStatus
  ) => {
    incidenceData.status = newStatus
  }

  const filteredButtons = useMemo(() => {
    return actionsButtonsData().filter((button) => {
      return (
        button.bookkeeperResponsibility === isBookkeeperView &&
        button.inHousePayroll === hasInhousePayroll &&
        isValidIncidenceStatus(incidence.status) &&
        button.currentStatuses.includes(incidence.status)
      )
    })
  }, [isBookkeeperView, hasInhousePayroll, incidence.status])

  if (!filteredButtons.length) {
    return <></>
  }

  return (
    <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
      {filteredButtons.map((data) => {
        return (
          <Box
            key={data.translationString}
            style={(filteredButtons.length > 2 && data.style) || undefined}
          >
            <Button
              type={data.type}
              onClick={() => changeToNewStatus(data.nextStatus)}
              disabled={data.canBeDisabled && ruleEvaluation?.validity === 'blocked'}
              iconName={data.icon}
            >
              {data.translationString}
            </Button>
          </Box>
        )
      })}
    </Box>
  )
}

export default observer(ActionsButtons)
